import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { Minus, Plus, X, ShoppingBag, Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartPage = () => {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const [loading, setLoading] = useState(false);
  const [shippingOption, setShippingOption] = useState('standard');
  const [saveEmail, setSaveEmail] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const shipping = subtotal === 0 ? 0 : (shippingOption === 'express' ? 25 : 12);
  const total = subtotal + shipping;

  const saveForLater = async (e) => {
    e.preventDefault();
    if (!saveEmail.includes('@') || items.length === 0) return;
    setSaveLoading(true);
    try {
      await axios.post(`${API}/cart/save`, {
        email: saveEmail,
        items: items.map(i => ({ product_id: i.id, variant_id: i.variantId || null, qty: i.qty })),
        origin_url: window.location.origin,
      });
      setSaved(true);
      toast.success('Saved. We\u2019ll email you a gentle reminder if you don\u2019t make it back.');
    } catch (err) {
      toast.error('Could not save cart. Please try again.');
    } finally {
      setSaveLoading(false);
    }
  };

  const checkout = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const payload = {
        items: items.map(i => ({
          product_id: i.id,
          variant_id: i.variantId || null,
          qty: i.qty,
          extras: i.extras || {},
        })),
        origin_url: window.location.origin,
      };
      const res = await axios.post(`${API}/payments/checkout`, payload);
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('Could not start checkout. Please try again.');
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Checkout failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-32 text-center">
        <ShoppingBag className="w-14 h-14 mx-auto mb-6 text-[#7a8b6b]" />
        <h1 className="font-serif-elegant text-4xl md:text-5xl mb-4">Your cart is quiet</h1>
        <p className="text-[#6f6a5f] mb-8">Fill it with a little wellness &mdash; you deserve it.</p>
        <Link to="/collections/all" className="btn-primary">Shop All Products</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
      <div className="text-center mb-12">
        <div className="uppercase tracking-[0.35em] text-xs text-[#7a8b6b] mb-3">Bag</div>
        <h1 className="font-serif-elegant text-5xl">Your Cart</h1>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-12">
        <div>
          <div className="grid grid-cols-[80px_1fr_auto_auto] gap-4 pb-3 border-b border-[#e5decf] uppercase tracking-widest text-xs text-[#6f6a5f]">
            <div className="col-span-2">Product</div>
            <div>Qty</div>
            <div className="text-right">Total</div>
          </div>
          <ul>
            {items.map(item => (
              <li key={item.lineId} className="grid grid-cols-[80px_1fr_auto_auto] gap-4 items-center py-6 border-b border-[#e5decf]">
                <img src={item.image} alt={item.title} className="w-20 h-20 object-cover" />
                <div>
                  <div className="font-medium mb-1">{item.title}</div>
                  <div className="text-sm text-[#7a8b6b]">${item.price.toFixed(2)}</div>
                  {item.extras?.deliverTo && (
                    <div className="text-xs text-[#6f6a5f] mt-1">Deliver to: {item.extras.deliverTo}</div>
                  )}
                  <button onClick={() => removeItem(item.lineId)} className="mt-2 inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[#6f6a5f] hover:text-[#7a3b2e]">
                    <X className="w-3 h-3" /> Remove
                  </button>
                </div>
                <div className="flex items-center border border-[#c9c1ad]">
                  <button onClick={() => updateQty(item.lineId, item.qty - 1)} className="p-2 hover:bg-[#efe9db]"><Minus className="w-3 h-3" /></button>
                  <span className="px-3 text-sm">{item.qty}</span>
                  <button onClick={() => updateQty(item.lineId, item.qty + 1)} className="p-2 hover:bg-[#efe9db]"><Plus className="w-3 h-3" /></button>
                </div>
                <div className="text-right font-medium">${(item.price * item.qty).toFixed(2)}</div>
              </li>
            ))}
          </ul>
        </div>

        <aside className="bg-[#efe9db] p-8 h-fit sticky top-28">
          <div className="font-serif-elegant text-2xl mb-6">Order Summary</div>

          {/* Shipping option */}
          <div className="mb-5">
            <div className="uppercase tracking-widest text-xs text-[#6f6a5f] mb-2">Shipping</div>
            <div className="space-y-2">
              <label className={`flex items-center justify-between gap-3 p-3 border cursor-pointer transition-colors ${shippingOption === 'standard' ? 'border-[#3a4a3b] bg-[#faf7f1]' : 'border-[#c9c1ad]'}`}>
                <div className="flex items-center gap-2">
                  <input type="radio" checked={shippingOption === 'standard'} onChange={() => setShippingOption('standard')} />
                  <div>
                    <div className="text-sm font-medium">Standard</div>
                    <div className="text-xs text-[#6f6a5f]">3&ndash;7 business days</div>
                  </div>
                </div>
                <div className="text-sm font-medium">$12.00</div>
              </label>
              <label className={`flex items-center justify-between gap-3 p-3 border cursor-pointer transition-colors ${shippingOption === 'express' ? 'border-[#3a4a3b] bg-[#faf7f1]' : 'border-[#c9c1ad]'}`}>
                <div className="flex items-center gap-2">
                  <input type="radio" checked={shippingOption === 'express'} onChange={() => setShippingOption('express')} />
                  <div>
                    <div className="text-sm font-medium">Express</div>
                    <div className="text-xs text-[#6f6a5f]">1&ndash;3 business days</div>
                  </div>
                </div>
                <div className="text-sm font-medium">$25.00</div>
              </label>
            </div>
          </div>

          <div className="space-y-3 text-sm mb-6">
            <div className="flex justify-between"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between">
              <span>Shipping</span>
              <span>${shipping.toFixed(2)}</span>
            </div>
            <div className="border-t border-[#c9c1ad] pt-3 flex justify-between text-base font-medium">
              <span>Total</span><span>${total.toFixed(2)}</span>
            </div>
          </div>
          <button onClick={checkout} disabled={loading} className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting...</> : <><Lock className="w-4 h-4" /> Secure Checkout</>}
          </button>
          <div className="mt-3 text-center text-xs text-[#6f6a5f]">
            Powered by Stripe. Use test card <span className="font-mono">4242 4242 4242 4242</span> in test mode.
          </div>
          <Link to="/collections/all" className="block text-center mt-4 text-sm link-underline">Continue shopping</Link>

          {/* Save cart for later */}
          {!saved ? (
            <form onSubmit={saveForLater} className="mt-6 pt-6 border-t border-[#c9c1ad]">
              <label className="uppercase tracking-widest text-xs text-[#6f6a5f] mb-2 block">Save your cart for later</label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  value={saveEmail}
                  onChange={(e) => setSaveEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="flex-1 bg-[#faf7f1] border border-[#c9c1ad] px-3 py-2 text-sm focus:outline-none focus:border-[#3a4a3b]"
                />
                <button
                  disabled={saveLoading || !saveEmail.includes('@')}
                  className="btn-outline whitespace-nowrap disabled:opacity-50"
                >
                  {saveLoading ? 'Saving...' : 'Email me'}
                </button>
              </div>
              <p className="text-[10px] text-[#6f6a5f] mt-2 leading-relaxed">We&rsquo;ll send you a gentle reminder if you don&rsquo;t come back within a couple hours. Unsubscribe anytime.</p>
            </form>
          ) : (
            <div className="mt-6 pt-6 border-t border-[#c9c1ad] text-sm text-[#3a4a3b] text-center">
              &#10004; Cart saved. See you soon.
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

export default CartPage;
